import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Grid3X3, List } from 'lucide-react';
import PageTitle from '../../components/shared/PageTitle';
import ProspectsStats from '../../components/prospects/ProspectsStats';
import ProspectsList from './ProspectsList';
import ProspectsKanban from '../../components/prospects/ProspectsKanban';
import ProspectsWidgetSubmissions from '../../components/prospects/ProspectsWidgetSubmissions';
import { useGetPromotersQuery } from '../../store/promoterApi';
import { Can } from '../../contexts/AbilityContext';

const Prospects: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'list' | 'kanban' | 'widget'>('kanban');
  const { data: promoters } = useGetPromotersQuery();
  const hasVerifiedPromoter = (promoters ?? []).some((p) => p.isEmailVerified === true);


  return (
    <div className="space-y-6">
      <PageTitle title={t('prospects.pageTitle')} />
      <ProspectsStats />

      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('prospects.title')}</h1>
        <div className="flex items-center space-x-3">
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
          <div className="flex items-center rounded-lg p-1" style={{ backgroundColor: '#E4E7EB' }}>
            <button
              onClick={() => setViewMode('kanban')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'kanban'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid3X3 className="w-4 h-4" />
              <span>{t('prospects.funnel')}</span>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4" />
              <span>{t('prospects.listView')}</span>
            </button>
            <button
              onClick={() => setViewMode('widget')}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === 'widget'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Plus className="w-4 h-4" />
              <span>{t('prospects.widgetSubmission')}</span>
            </button>
          </div>
          </div>
          <Can I="create" a="Prospect">
            {!hasVerifiedPromoter ? (
              <div className="flex flex-col items-end gap-1">
                <button
                  disabled
                  className="btn-primary flex items-center space-x-2 opacity-60 cursor-not-allowed"
                >
                  <Plus className="w-5 h-5" />
                  <span>{t('prospects.add')}</span>
                </button>
                <p className="text-xs text-amber-700 max-w-[240px] text-right leading-snug">
                  {t('prospects.needVerifiedPromoter')}
                </p>
              </div>
            ) : (
              <button
                onClick={() => navigate('/add-prospect')}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>{t('prospects.add')}</span>
              </button>
            )}
          </Can>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b">
          <h2 className="text-lg font-semibold">
            {viewMode === 'list' ? t('prospects.listTitle') :
             viewMode === 'kanban' ? t('prospects.funnelTitle') :
             t('prospects.widgetSubmissionsTitle')}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {viewMode === 'list'
              ? t('prospects.listDescription')
              : viewMode === 'kanban'
              ? t('prospects.funnelDescription')
              : t('prospects.widgetDescription')
            }
          </p>
        </div>

        <div className="p-6">
          {viewMode === 'list' ? <ProspectsList /> : 
           viewMode === 'kanban' ? <ProspectsKanban /> : 
           <ProspectsWidgetSubmissions />}
        </div>
      </div>
    </div>
  );
};

export default Prospects;